import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Lock, Sparkles, CheckCircle2, Info, ArrowRightLeft, UserCog, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { usePreferences } from '../../context/PreferencesContext';
import AuthModal from '../auth/AuthModal';
import { toast } from 'sonner';

const UpgradeModal = ({ isOpen, onClose, feature = 'answers', subscribedGrade = null, targetGrade = null }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { updatePreferences } = usePreferences();
  const [showAuth, setShowAuth] = useState(false);
  const [switching, setSwitching] = useState(false);

  const features = {
    answers: {
      title: 'Unlock All Answers',
      description: 'Get instant access to detailed answers for all questions',
      benefits: [
        'View answers for all questions',
        'Detailed explanations',
        'Learn from correct solutions',
        'Improve your preparation'
      ]
    },
    'practice-tests': {
      title: 'Unlock Practice Tests',
      description: 'Take unlimited mock tests to assess your preparation',
      benefits: [
        'Unlimited practice tests',
        'Exam-like environment',
        'Timed tests',
        'Instant results'
      ]
    },
    'progress-tracker': {
      title: 'Unlock Progress Tracker',
      description: 'Track your learning journey and monitor your improvement',
      benefits: [
        'Detailed progress analytics',
        'Performance graphs',
        'Weak area identification',
        'Study recommendations'
      ]
    }
  };

  const currentFeature = features[feature] || features.answers;

  // Three distinct states the modal can render:
  //   grade-mismatch : logged-in + paid for a *different* grade than the one being viewed
  //   grade-unset    : logged-in + no grade preference set at all
  //   upgrade        : default — the classic "you're free, buy a plan" prompt
  const isLoggedIn = isAuthenticated();
  const hasSubscriptionButWrongGrade =
    isLoggedIn &&
    subscribedGrade &&
    subscribedGrade !== 'all' &&
    targetGrade &&
    subscribedGrade !== targetGrade;
  const gradeUnset = isLoggedIn && !targetGrade && !subscribedGrade;

  const handleUpgrade = () => {
    if (!isLoggedIn) {
      // Guest user - show sign up modal
      setShowAuth(true);
      return;
    }
    // Logged in user - go to plans with target grade
    onClose();
    if (targetGrade) {
      navigate(`/plans?grade=${encodeURIComponent(targetGrade)}`);
    } else {
      navigate('/plans');
    }
  };

  const handleSwitchGrade = async () => {
    if (!subscribedGrade) return;
    setSwitching(true);
    try {
      await updatePreferences({ grade: subscribedGrade });
      toast.success(`Switched to ${subscribedGrade}`);
      onClose();
      // Reload so grade-scoped fetches re-run with the new preference.
      // Cheaper than teaching every consumer to re-fetch on grade change.
      window.location.reload();
    } catch (err) {
      toast.error('Could not switch grade. Please try from Profile → Preferences.');
    } finally {
      setSwitching(false);
    }
  };

  const handleGoToPreferences = () => {
    onClose();
    navigate('/profile');
  };

  // ---------- Render: grade-unset state ----------
  if (gradeUnset) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md" data-testid="paid-gate-modal-grade-unset">
          <DialogHeader>
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
              <UserCog className="h-6 w-6 text-amber-600" />
            </div>
            <DialogTitle className="text-center text-xl">Set your exam grade first</DialogTitle>
            <DialogDescription className="text-center">
              Please pick a grade in Profile → Preferences to start using this feature.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1" data-testid="paid-gate-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={handleGoToPreferences}
              className="flex-1 bg-[#0A7EA4] hover:bg-[#08678A]"
              data-testid="paid-gate-open-profile-btn"
            >
              <UserCog className="w-4 h-4 mr-2" />
              Open Profile
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // ---------- Render: grade-mismatch state ----------
  if (hasSubscriptionButWrongGrade) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md" data-testid="paid-gate-modal-grade-mismatch">
          <DialogHeader>
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
              <ArrowRightLeft className="h-6 w-6 text-blue-600" />
            </div>
            <DialogTitle className="text-center text-xl">This plan doesn&apos;t cover this grade</DialogTitle>
            <DialogDescription className="text-center">
              Your active subscription is for <span className="font-semibold text-slate-900">{subscribedGrade}</span>,
              {' '}but you&apos;re currently viewing <span className="font-semibold text-slate-900">{targetGrade}</span> content.
              You can either switch back to the grade your plan covers, or purchase a plan for {targetGrade} as well.
            </DialogDescription>
          </DialogHeader>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 my-2 flex items-start gap-2">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-blue-800">
              Switching grades takes one click and keeps your existing plan. You can switch back any time from Profile → Preferences.
            </p>
          </div>

          {/* Actions: primary = Switch (one-click, no cost); secondary = Buy new plan. */}
          <div className="flex flex-col gap-2 pt-2">
            <Button
              onClick={handleSwitchGrade}
              disabled={switching}
              className="w-full bg-[#0A7EA4] hover:bg-[#08678A]"
              data-testid="paid-gate-switch-grade-btn"
            >
              {switching ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ArrowRightLeft className="w-4 h-4 mr-2" />
              )}
              Switch to {subscribedGrade}
            </Button>
            <Button
              variant="outline"
              onClick={handleUpgrade}
              disabled={switching}
              className="w-full"
              data-testid="paid-gate-buy-other-grade-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Buy plan for {targetGrade} instead
            </Button>
            <Button
              variant="ghost"
              onClick={onClose}
              disabled={switching}
              className="w-full text-slate-500"
              data-testid="paid-gate-cancel-btn"
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // ---------- Render: default upgrade state ----------
  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="paid-gate-modal-upgrade">
        <DialogHeader>
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
            <Lock className="h-6 w-6 text-blue-600" />
          </div>
          <DialogTitle className="text-center text-xl">
            {currentFeature.title}
          </DialogTitle>
          <DialogDescription className="text-center">
            {currentFeature.description}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <p className="font-semibold mb-3 text-sm">Premium Benefits{targetGrade ? ` for ${targetGrade}` : ''}:</p>
            <div className="space-y-2">
              {currentFeature.benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
              data-testid="paid-gate-cancel-btn"
            >
              Maybe Later
            </Button>
            <Button
              onClick={handleUpgrade}
              className="flex-1"
              data-testid="paid-gate-upgrade-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {isLoggedIn ? 'Upgrade Now' : 'Sign Up to Upgrade'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    
    <AuthModal open={showAuth} onClose={() => setShowAuth(false)} />
    </>
  );
};

export default UpgradeModal;
